import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Highlight } from 'react-instantsearch';

import { CoreLink } from '@uncinc/react-drupal-core';

import { HOSTNAME_ANNUAL_REPORT, NODE_TYPE_ANNUAL_REPORT_LANDING_PAGE } from '../../../config/constants';
import type { SearchAllContentHit } from '../types';

const useHighlightedAttribute = (
  hit: SearchAllContentHit,
  attributes: string[],
) => useMemo(() => {
  const attribute = attributes.find((field) => field in hit);

  if (attribute) {
    return <Highlight attribute={attribute} hit={hit} />;
  }
  return null;
}, [hit, attributes]);

const Hit: React.FC<SearchAllContentHit> = (hit) => {
  const {
    activity_name: activityName,
    combined_created: combinedCreated,
    combined_types: combinedTypes,
    document_url: documentUrl,
    node_image_ratio_4_3_sm: nodeImageRatio43Sm,
    node_media_item_ratio_4_3_sm: nodeMediaItemRatio43Sm,
    paragraph_video_url: paragraphVideoUrl,
    term_image_ratio_4_3_sm: termImageRatio43Sm,
    url,
    vacancy_type: vacancyType,
    theme,
  } = hit;

  const { t } = useTranslation();
  const entityType = combinedTypes ? combinedTypes[0] : 'media';

  // Try to get an indexed image.
  const image = nodeMediaItemRatio43Sm || nodeImageRatio43Sm || termImageRatio43Sm;

  const highlightedTitle = useHighlightedAttribute(hit, ['title', 'name', 'document_title', 'filename']);
  const highlightedBody = useHighlightedAttribute(hit, ['body', 'description']);
  const formattedDate = (combinedCreated && combinedCreated.length > 0) && format(new Date(Number(combinedCreated[0]) * 1000), 'd MMMM y');

  let hitUrl = url[0];

  if (documentUrl) {
    [hitUrl] = documentUrl;
  }

  if (combinedTypes && combinedTypes[0] === NODE_TYPE_ANNUAL_REPORT_LANDING_PAGE) {
    hitUrl = `${HOSTNAME_ANNUAL_REPORT}${hitUrl}`;
  }

  return (
    <CoreLink href={hitUrl}>
      <div className="SearchAllContentResults row">
        <div className="SearchAllContentResults__content col">
          <div className="SearchAllContentResults__types">
            <span className={`SearchAllContentResults__types_${entityType}`}>
              {t(`components.search.results.label.${entityType}`)}
            </span>
            {activityName && (
              <>
                <span>&bull;</span>
                <span className="SearchAllContentResults__types_activity">
                  {activityName}
                </span>
              </>
            )}
            {vacancyType && (
              <>
                <span>&bull;</span>
                <span className="SearchAllContentResults__types_vacancy">
                  {vacancyType}
                </span>
              </>
            )}
            {theme && theme.map((themeItem) => (
              <React.Fragment key={themeItem}>
                <span>&bull;</span>
                <span className="SearchAllContentResults__types_theme">
                  {themeItem}
                </span>
              </React.Fragment>
            ))}
            {/* Has a video paragraph */}
            {paragraphVideoUrl && (
              <>
                <span>&bull;</span>
                <span className="SearchAllContentResults__types_badge SearchAllContentResults__types_badge--video">
                  {t('components.search.results.badge.video')}
                </span>
              </>
            )}
          </div>

          <div className="SearchAllContentResults__text">
            <h4 className="SearchAllContentResults__title">
              {highlightedTitle}
            </h4>

            {highlightedBody && (
              <div className="SearchAllContentResults__body">
                {highlightedBody}
              </div>
            )}
          </div>

          {formattedDate && (
            <div className="SearchAllContentResults__created">
              <span>{formattedDate}</span>
            </div>
          )}
        </div>

        {image && (
          <div className="SearchAllContentResults__media col-3">
            <img src={image[0]} alt="" />
          </div>
        )}
      </div>
    </CoreLink>
  );
};

export default Hit;
